import { useState } from 'react';
import { useReportBuilderStore } from '../../stores/reportBuilderStore';
import type { CompareType, PeriodComparisonSpec } from '../../types/reportBuilder';
import { COMPARE_TYPE_LABELS } from '../../types/reportBuilder';
import { X, CalendarBlank } from '@phosphor-icons/react';

const COMPARE_TYPES: CompareType[] = ['yoy', 'mom', 'qoq', 'custom'];

export default function PeriodComparisonPanel({ onClose }: { onClose: () => void }) {
  const { periodComparison, setPeriodComparison, getDateColumns, selectedMeasures } = useReportBuilderStore();

  const dateColumns = getDateColumns();
  const hasMeasures = selectedMeasures.length > 0;

  const [dateColumn, setDateColumn] = useState(periodComparison?.date_column ?? dateColumns[0]?.refKey ?? '');
  const [compareType, setCompareType] = useState<CompareType>(periodComparison?.compare_type ?? 'yoy');
  const [currentStart, setCurrentStart] = useState(periodComparison?.current_start ?? '');
  const [currentEnd, setCurrentEnd] = useState(periodComparison?.current_end ?? '');
  const [previousStart, setPreviousStart] = useState(periodComparison?.previous_start ?? '');
  const [previousEnd, setPreviousEnd] = useState(periodComparison?.previous_end ?? '');

  const isEnabled = !!periodComparison;

  const handleEnable = () => {
    if (!dateColumn || !hasMeasures) return;
    const spec: PeriodComparisonSpec = {
      date_column: dateColumn,
      compare_type: compareType,
    };
    if (compareType === 'custom') {
      spec.current_start = currentStart;
      spec.current_end = currentEnd;
      spec.previous_start = previousStart;
      spec.previous_end = previousEnd;
    }
    setPeriodComparison(spec);
  };

  const handleDisable = () => {
    setPeriodComparison(null);
  };

  const isCustomValid = compareType !== 'custom' || (currentStart && currentEnd && previousStart && previousEnd);
  const canEnable = dateColumn && hasMeasures && isCustomValid;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="w-[420px] rounded-lg bg-white shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-800">
            <CalendarBlank size={14} /> Period Comparison
          </h3>
          <button onClick={onClose} className="rounded p-1 hover:bg-gray-100"><X size={14} /></button>
        </div>

        <div className="space-y-3 px-4 py-3">
          {!hasMeasures && (
            <div className="rounded bg-amber-50 px-3 py-2 text-xs text-amber-700">
              Add at least one measure to enable period comparison.
            </div>
          )}

          {isEnabled && (
            <div className="rounded border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-700">
              Period comparison is active ({COMPARE_TYPE_LABELS[periodComparison!.compare_type]})
            </div>
          )}

          <div>
            <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Date Column</label>
            <select value={dateColumn} onChange={e => setDateColumn(e.target.value)}
              className="w-full rounded border border-gray-300 px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none">
              <option value="">Select a date column...</option>
              {dateColumns.map(c => (
                <option key={c.refKey} value={c.refKey}>{c.sourceModelName} / {c.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Comparison Type</label>
            <div className="flex gap-1">
              {COMPARE_TYPES.map(ct => (
                <button key={ct} onClick={() => setCompareType(ct)}
                  className={`rounded border px-2 py-1 text-[10px] ${compareType === ct ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                  {COMPARE_TYPE_LABELS[ct]}
                </button>
              ))}
            </div>
          </div>

          {compareType === 'custom' && (
            <div className="space-y-2 rounded border border-gray-200 bg-gray-50 p-3">
              <div>
                <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Current Period</label>
                <div className="flex gap-1">
                  <input type="date" value={currentStart} onChange={e => setCurrentStart(e.target.value)}
                    className="flex-1 rounded border border-gray-300 px-2 py-1 text-xs" />
                  <span className="self-center text-[10px] text-gray-400">to</span>
                  <input type="date" value={currentEnd} onChange={e => setCurrentEnd(e.target.value)}
                    className="flex-1 rounded border border-gray-300 px-2 py-1 text-xs" />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Previous Period</label>
                <div className="flex gap-1">
                  <input type="date" value={previousStart} onChange={e => setPreviousStart(e.target.value)}
                    className="flex-1 rounded border border-gray-300 px-2 py-1 text-xs" />
                  <span className="self-center text-[10px] text-gray-400">to</span>
                  <input type="date" value={previousEnd} onChange={e => setPreviousEnd(e.target.value)}
                    className="flex-1 rounded border border-gray-300 px-2 py-1 text-xs" />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t px-4 py-3">
          {isEnabled && (
            <button onClick={handleDisable}
              className="rounded border border-red-300 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50">
              Disable
            </button>
          )}
          <button onClick={onClose}
            className="rounded border border-gray-300 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50">Close</button>
          <button onClick={handleEnable} disabled={!canEnable}
            className="rounded bg-primary px-3 py-1.5 text-xs text-white hover:bg-primary/90 disabled:opacity-50">
            {isEnabled ? 'Update' : 'Enable'}
          </button>
        </div>
      </div>
    </div>
  );
}
